import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { useStore } from '../data/store'
import { useI18n } from '../i18n'
import { buildInvoiceDoc, type DocModel } from '../lib/documents'
import { bundleFileName, summariseBundle, type NumberSeries } from '../lib/bookkeeping'
import { supabase } from '../lib/supabase'
import { Button, Card, EmptyState } from '../ui/kit'
import { useDocScale } from '../ui/useDocScale'
import { usePrintFileName } from '../ui/usePrintFileName'
import { Belegblatt } from './Belegblatt'

/**
 * Das Belegbündel für die Kanzlei.
 *
 * WARUM ES DAS GIBT: eine CSV ohne Rechnungen ist für eine Buchhaltung wertlos. Die
 * Kanzlei bucht gegen Belege, nicht gegen eine Liste. Bisher hätte der Vermieter jede
 * Rechnung einzeln öffnen und einzeln als PDF sichern müssen — bei achtzig Buchungen
 * im Jahr sind das achtzig Dateien und ein verlorener Abend.
 *
 * WARUM ES EIN EINZIGES DOKUMENT IST: StaySuite hat keinen PDF-Erzeuger und soll
 * keinen bekommen. Ein Chromium in einer Edge Function für ein 29-€-Produkt ist nicht
 * verhältnismäßig. Der Druckdialog des Browsers kann alles, was gebraucht wird — er
 * kann nur eine Datei auf einmal. Also besteht das Bündel aus einem Deckblatt und
 * allen Belegen dahinter, jeder auf eigenen Seiten. Einmal drucken, eine Datei.
 *
 * WARUM DIE BELEGE HIER NICHT MEHR NOCHMAL GEBAUT WERDEN: sie wurden es, und das war
 * die zweite von drei Fassungen desselben Blattes. Seit Vorgang 008 zeichnet
 * `views/Belegblatt.tsx` den Beleg, und dieses Bündel benutzt genau diese Komponente.
 * Was hier gedruckt wird, ist damit nicht mehr „sieht aus wie", sondern IST das, was
 * der Gast bekommen hat.
 */
export default function InvoiceBundle() {
  const { from, to } = useParams<{ from: string; to: string }>()
  const navigate = useNavigate()
  const store = useStore()
  const { lang, formatMoney } = useI18n()
  const de = lang === 'de'
  const { viewportRef, sheetRef } = useDocScale<HTMLDivElement, HTMLDivElement>([from, to, store.db])

  const sum = useMemo(() => summariseBundle({
    invoices: store.db.invoices,
    invoiceItems: store.db.invoice_items,
    from: from ?? '',
    to: to ?? '',
  }), [store.db.invoices, store.db.invoice_items, from, to])

  /**
   * Die RKSV-Signaturen aller Belege des Bündels, in einer Abfrage.
   *
   * Ein signierter Beleg ohne seinen QR-Code ist kein signierter Beleg mehr — was im
   * Bündel steht, muss dasselbe sein wie das, was der Gast in der Hand hatte. Eine
   * Abfrage je Rechnung wäre bei einem Jahresbündel ein Sturm auf die Datenbank,
   * deshalb `in(...)` über alle Nummern auf einmal.
   */
  const [rksv, setRksv] = useState<Record<string, string>>({})
  const docIds = useMemo(() => sum.docs.map((d) => d.id).join(','), [sum.docs])
  useEffect(() => {
    let alive = true
    setRksv({})
    if (!store.live || !supabase || !docIds) return
    void supabase.from('rksv_receipts')
      .select('invoice_id, qr_payload').in('invoice_id', docIds.split(','))
      .then(({ data }) => {
        if (!alive || !data) return
        const map: Record<string, string> = {}
        for (const r of data) if (r.qr_payload) map[String(r.invoice_id)] = String(r.qr_payload)
        setRksv(map)
      })
    return () => { alive = false }
  }, [docIds, store.live])

  // Zeilen und Zahlungen einmal gruppieren statt je Beleg zu filtern: bei einem
  // Jahresbündel wäre das sonst quadratisch, und zwar auf einem Telefon.
  const models = useMemo<{ id: string; model: DocModel }[]>(() => {
    const itemsBy = new Map<string, typeof store.db.invoice_items>()
    for (const it of store.db.invoice_items) {
      const list = itemsBy.get(it.invoice_id) ?? []
      list.push(it)
      itemsBy.set(it.invoice_id, list)
    }
    const paysBy = new Map<string, typeof store.db.payments>()
    for (const p of store.db.payments) {
      if (!p.booking_id) continue
      if (p.kind === 'security_deposit' || p.kind === 'security_deposit_refund') continue
      const list = paysBy.get(p.booking_id) ?? []
      list.push(p)
      paysBy.set(p.booking_id, list)
    }

    return sum.docs.map((invoice) => {
      const booking = store.db.bookings.find((b) => b.id === invoice.booking_id)
      const unit = booking ? store.unitById(booking.unit_id) : undefined
      const pays = invoice.booking_id ? paysBy.get(invoice.booking_id) ?? [] : []
      const last = [...pays].sort((a, b) => a.received_on.localeCompare(b.received_on)).at(-1)
      return {
        id: invoice.id,
        model: buildInvoiceDoc(invoice, itemsBy.get(invoice.id) ?? [], {
          tenant: store.db.tenant,
          guest: store.guestById(invoice.guest_id) ?? undefined,
          booking,
          unit,
          property: store.db.properties.find((p) => p.id === unit?.property_id),
          paid: invoice.booking_id ? store.paidFor(invoice.booking_id) : invoice.paid_amount,
          prepaid: pays.filter((p) => p.kind === 'prepayment').reduce((a, p) => a + p.amount, 0),
          ...(last ? { paidOn: last.received_on, paidMethod: last.method } : {}),
        }),
      }
    })
  }, [sum.docs, store])

  const business = store.db.tenant.legal_name || store.db.tenant.name
  usePrintFileName(from && to ? bundleFileName(business, from, to) : null)

  const fmtDate = (iso: string) => new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(iso + 'T00:00:00'))

  if (!from || !to) {
    return <div className="p-4"><Card><EmptyState title={de ? 'Kein Zeitraum' : 'No period'} /></Card></div>
  }

  return (
    <div className="min-h-full">
      <div className="no-print sticky top-0 z-10 bg-surface border-b border-border
                      px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {de ? 'Belegbündel' : 'Document bundle'} {fmtDate(from)} – {fmtDate(to)}
          </div>
          <div className="text-xs text-muted">
            {sum.docs.length} {de
              ? (sum.docs.length === 1 ? 'Beleg' : 'Belege')
              : (sum.docs.length === 1 ? 'document' : 'documents')}
            {' · '}
            {de ? 'Deckblatt + je Beleg eigene Seiten' : 'cover sheet plus one page per document'}
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={() => window.print()}>
          <Printer size={16} /> PDF
        </Button>
      </div>

      {sum.docs.length === 0 ? (
        <div className="p-4">
          <Card>
            <EmptyState title={de
              ? 'Keine festgeschriebenen Belege in diesem Zeitraum.'
              : 'No issued documents in this period.'} />
          </Card>
        </div>
      ) : (
        <div className="p-4 no-print-padding doc-viewport" ref={viewportRef}>
          <div className="doc-bundle" ref={sheetRef}>
            {/* ---------- Deckblatt ---------- */}
            <article className="doc-sheet">
              <header className="doc-head">
                <div>
                  {store.db.tenant.logo_url && (
                    <img className="doc-logo" src={store.db.tenant.logo_url} alt="" />
                  )}
                  <div className="doc-issuer-name">{business}</div>
                  {store.db.tenant.address && <div>{store.db.tenant.address}</div>}
                  {store.db.tenant.vat_id && (
                    <div>{de ? 'UID' : 'VAT ID'}: {store.db.tenant.vat_id}</div>
                  )}
                  {store.db.tenant.email && <div>{store.db.tenant.email}</div>}
                </div>
                <div className="doc-title-block">
                  <h1>{de ? 'Belegbündel' : 'Document bundle'}</h1>
                  <table>
                    <tbody>
                      <tr>
                        <td>{de ? 'Zeitraum' : 'Period'}</td>
                        <td>{fmtDate(from)} – {fmtDate(to)}</td>
                      </tr>
                      <tr>
                        <td>{de ? 'Belege' : 'Documents'}</td>
                        <td>{sum.docs.length}</td>
                      </tr>
                      <tr>
                        <td>{de ? 'Erstellt am' : 'Created'}</td>
                        <td>{fmtDate(new Date().toISOString().slice(0, 10))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </header>

              <p className="doc-intro" style={{ marginTop: '12mm' }}>
                {de
                  ? 'Diese Datei enthält alle festgeschriebenen Rechnungen und Gutschriften des Zeitraums, jede auf eigenen Seiten. Entwürfe sind nicht enthalten — sie tragen keine Nummer und sind kein Beleg. Stornierte Rechnungen sind enthalten, weil sie eine Nummer tragen; die zugehörige Gutschrift steht mit im Bündel.'
                  : 'This file contains every issued invoice and credit note of the period, each on its own pages. Drafts are excluded — they carry no number and are not documents. Cancelled invoices are included because they carry a number; the matching credit note is in the bundle too.'}
              </p>

              {/* ---------- Nummernkreis und Lückenprüfung ----------
                  Ganz oben, weil eine Kanzlei genau das zuerst prüft: ob die
                  Rechnungsnummern lückenlos sind (§ 11 UStG AT, § 14 UStG DE). */}
              <section className="doc-payment">
                <h2>{de ? 'Nummernkreis' : 'Number range'}</h2>
                {sum.series.map((s) => (
                  <SeriesBlock key={s.prefix} series={s} de={de} fmtDate={fmtDate} />
                ))}
              </section>

              {/* ---------- Summen je Steuersatz ---------- */}
              <section className="doc-payment">
                <h2>{de ? 'Summen je Steuersatz' : 'Totals by VAT rate'}</h2>
                <table className="doc-lines">
                  <thead>
                    <tr>
                      <th className="l">{de ? 'Satz' : 'Rate'}</th>
                      <th className="r">{de ? 'Netto' : 'Net'}</th>
                      <th className="r">{de ? 'USt.' : 'VAT'}</th>
                      <th className="r">{de ? 'Brutto' : 'Gross'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sum.vatRows.map((r) => (
                      <tr key={r.rate}>
                        <td className="l">{r.rate} %</td>
                        <td className="r">{formatMoney(r.net)}</td>
                        <td className="r">{formatMoney(r.vat)}</td>
                        <td className="r">{formatMoney(r.gross)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="l"><strong>{de ? 'Summe' : 'Total'}</strong></td>
                      <td className="r"><strong>{formatMoney(sum.net)}</strong></td>
                      <td className="r"><strong>{formatMoney(sum.vat)}</strong></td>
                      <td className="r"><strong>{formatMoney(sum.gross)}</strong></td>
                    </tr>
                  </tbody>
                </table>
                <p className="doc-note">
                  {de
                    ? `Gezählt sind ${sum.invoiceCount} ${sum.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'} nach Rechnungsdatum. Stornierte Rechnungen und Gutschriften sind hier NICHT enthalten und stehen unten.`
                    : `Counted are ${sum.invoiceCount} ${sum.invoiceCount === 1 ? 'invoice' : 'invoices'} by invoice date. Cancelled invoices and credit notes are NOT included here and appear below.`}
                </p>
              </section>

              {/* ---------- Was kein Umsatz ist ---------- */}
              <section className="doc-payment">
                <h2>{de ? 'Getrennt zu sehen' : 'Listed separately'}</h2>
                <table className="doc-bank">
                  <tbody>
                    <tr>
                      <td>{de ? 'Gutschriften (Storno)' : 'Credit notes'}</td>
                      <td>
                        {sum.creditCount} · −{formatMoney(sum.creditedGross)}
                      </td>
                    </tr>
                    <tr>
                      <td>{de ? 'Stornierte Rechnungen' : 'Cancelled invoices'}</td>
                      <td>
                        {sum.cancelledCount} · {formatMoney(sum.cancelledGross)}
                      </td>
                    </tr>
                    <tr>
                      <td>{de ? 'Ortstaxe (durchlaufender Posten)' : 'Tourist tax (pass-through)'}</td>
                      <td>{formatMoney(sum.touristTax)}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="doc-note">
                  {de
                    ? 'Die Ortstaxe ist kein Ertrag des Betriebs, sondern wird für die Gemeinde eingehoben. Sie steckt oben mit 0 % im Netto und ist hier nur noch einmal beziffert.'
                    : 'Tourist tax is not turnover — it is collected on behalf of the municipality. It is part of the 0 % net figure above and merely restated here.'}
                </p>
              </section>

              {/* ---------- Inhaltsverzeichnis ----------
                  Damit die Kanzlei prüfen kann, ob die Datei vollständig angekommen
                  ist, ohne durch achtzig Seiten zu blättern. */}
              <section className="doc-payment">
                <h2>{de ? 'Inhalt' : 'Contents'}</h2>
                <table className="doc-lines">
                  <thead>
                    <tr>
                      <th className="l">{de ? 'Nummer' : 'Number'}</th>
                      <th className="l">{de ? 'Datum' : 'Date'}</th>
                      <th className="l">{de ? 'Empfänger' : 'Recipient'}</th>
                      <th className="r">{de ? 'Brutto' : 'Gross'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sum.docs.map((d) => (
                      <tr key={d.id}>
                        <td className="l">
                          {d.number}
                          {d.type === 'credit_note' && ` · ${de ? 'Gutschrift' : 'Credit note'}`}
                          {d.status === 'cancelled' && ` · ${de ? 'storniert' : 'cancelled'}`}
                        </td>
                        <td className="l">{fmtDate(d.issue_date)}</td>
                        <td className="l">{store.guestName(d.guest_id)}</td>
                        <td className="r">{formatMoney(d.total_gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <footer className="doc-print-foot">
                {[business, de ? 'Belegbündel' : 'Document bundle', `${from} – ${to}`]
                  .filter(Boolean).join(' · ')}
              </footer>
            </article>

            {/* ---------- die Belege selbst ---------- */}
            {models.map(({ id, model }) => (
              <Belegblatt key={id} model={model} rksvQr={rksv[id] ?? null} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Ein Nummernkreis mit seinem Befund.
 *
 * Die Lücken werden benannt, nicht verschwiegen. Eine fehlende Nummer ist meistens
 * harmlos — die Rechnung liegt im Nachbarmonat —, aber das kann nur jemand feststellen,
 * der beide Fälle auseinanderhält. Deshalb steht bei jeder Lücke dabei, ob die Nummer
 * anderswo existiert oder in den Daten überhaupt nicht vorkommt.
 */
function SeriesBlock({ series, de, fmtDate }: {
  series: NumberSeries; de: boolean; fmtDate: (iso: string) => string
}) {
  const clean = series.gapCount === 0
  return (
    <div style={{ marginBottom: '4mm' }}>
      <p>
        <strong>{series.first} – {series.last}</strong>
        {' · '}
        {series.count} {de
          ? (series.count === 1 ? 'Beleg' : 'Belege')
          : (series.count === 1 ? 'document' : 'documents')}
      </p>
      {clean ? (
        <p className="doc-paid">
          {de
            ? 'Nummernfolge lückenlos — jede Nummer zwischen der ersten und der letzten ist als Beleg enthalten.'
            : 'Sequence complete — every number between the first and the last is present as a document.'}
        </p>
      ) : (
        <>
          <p className="doc-paid">
            {de
              ? `${series.gapCount} ${series.gapCount === 1 ? 'Nummer fehlt' : 'Nummern fehlen'} in dieser Folge:`
              : `${series.gapCount} ${series.gapCount === 1 ? 'number is' : 'numbers are'} missing from this sequence:`}
          </p>
          <table className="doc-bank" style={{ marginTop: '2mm' }}>
            <tbody>
              {series.gaps.map((g) => (
                <tr key={g.number}>
                  <td>{g.number}</td>
                  <td>
                    {g.kind === 'elsewhere'
                      ? (de
                        ? `Beleg vorhanden, datiert auf ${fmtDate(g.issueDate!)} — liegt außerhalb dieses Zeitraums`
                        : `Document exists, dated ${fmtDate(g.issueDate!)} — outside this period`)
                      : (de
                        ? 'Kommt in den Daten dieses Betriebs nicht vor'
                        : 'Does not appear in this business’s records')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {series.gaps.length < series.gapCount && (
            <p className="doc-note">
              {de
                ? `… und ${series.gapCount - series.gaps.length} weitere. Bei so vielen Lücken stimmt in der Regel der Nummernkreis nicht — bitte mit der Kanzlei klären.`
                : `… and ${series.gapCount - series.gaps.length} more. That many gaps usually mean the numbering itself is wrong — please clarify with your accountant.`}
            </p>
          )}
        </>
      )}
    </div>
  )
}
